import { useAuth } from '../contexts/AuthContext';

/**
 * Utility functions for organization-aware navigation
 */

/**
 * Creates a path with organization ID prefix
 * @param path - The base path (e.g., '/map', '/projects')
 * @param orgId - The organization ID
 * @returns The full path with organization prefix
 */
export const createOrgPath = (path: string, orgId: string): string => {
  // Remove leading slash if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `/organization/${orgId}/${cleanPath}`;
};

/**
 * Extracts the base path from an organization-aware path
 * @param orgPath - The full organization path (e.g., '/organization/123/map')
 * @returns The base path (e.g., '/map')
 */
export const extractBasePath = (orgPath: string): string => {
  const pathSegments = orgPath.split('/');
  if (pathSegments[1] === 'organization' && pathSegments.length > 3) {
    return '/' + pathSegments.slice(3).join('/');
  }
  return orgPath;
};

/**
 * Hook to get organization-aware navigation functions
 */
export const useOrgNavigation = () => {
  const { user } = useAuth();
  
  const navigateToOrgPath = (path: string): string => {
    if (!user?.orgId) {
      console.error('Organization ID not available for user:', user);
      throw new Error('Organization ID not available');
    }
    const orgPath = createOrgPath(path, user.orgId);
    console.log('Created org path:', orgPath, 'from path:', path, 'orgId:', user.orgId);
    return orgPath;
  };

  const getOrgIdFromPath = (pathname: string): string | null => {
    const pathSegments = pathname.split('/');
    if (pathSegments[1] === 'organization' && pathSegments[2]) {
      return pathSegments[2];
    }
    return null;
  };

  const isOrgPath = (pathname: string): boolean => {
    return pathname.startsWith('/organization/');
  };

  return {
    navigateToOrgPath,
    getOrgIdFromPath,
    isOrgPath,
    extractBasePath,
    orgId: user?.orgId
  };
};
